import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector } from 'reselect';
import { DateTime } from 'luxon';
import { Map as ImmutableMap, List, Record } from 'immutable';
import type RootState from 'companion-app-components/utils/redux-store/rootState';

import { selectorsHelpers } from 'companion-app-components/flux/core';
import { accountsSelectors } from 'companion-app-components/flux/accounts';
import { configFeatureFlagsSelectors } from 'companion-app-components/flux/config-feature-flags';
import { getLogger } from 'companion-app-components/utils/core';
import { authSelectors } from 'companion-app-components/flux/auth';
import { transactionsTransformers, transactionsUtils } from 'companion-app-components/flux/transactions';
import { Payee } from 'components/QuickenControls/PayeeField/types';

import { getLoadPending as getTransactionsLoadPending } from 'data/transactions/selectors';
import { getMemorizedRulesById } from 'companion-app-components/flux/memorized-rules/memorizedRulesSelectors';
import { getFieldString } from 'data/transactions/searchFilter';
import { ClientConfigFlags } from 'utils/clientConfigFlags';
import { isApplyMemorizedRulesEnabled } from 'data/preferences/selectors';

import { REDUCER_KEY } from './reducer';
import { getSplitCategoryNames } from './utils';

const log = getLogger('data/payees/selectors');

export const getLoadPending = (state: RootState) => accountsSelectors.getLoadPending(state) || getTransactionsLoadPending(state);

export const getPayeesStore = (state: RootState) => state[REDUCER_KEY] as any;

export const getPayeeTransactionsById = (state: RootState) => {
  const newState = getPayeesStore(state);
  return newState.payeesByAccountId;
};

export const getPayeesNameList = createSelector(
  getPayeeTransactionsById,
  (payeesById) => {

    let ret: List<any> | null = null;
    const payeeNames = ImmutableMap().withMutations((map) => {
      payeesById.forEach((account) => account.forEach((payee: Payee) => {
        const existing = map.get(payee.name) as { id: string; name: string; count: number } | undefined;
        map.set(payee.name, { id: payee.id, name: payee.name, count: payee.count + (existing?.count || 0) });
      }));
      return map;
    });
    if (payeeNames) {
      ret = List(payeeNames.toList().toJS()).sort((payee1: Payee, payee2: Payee) => payee2.count - payee1.count);
    }
    return ret;
  },
);

/**
 * Get payee data for the requested account node
 */

export const getPayeesForAccounts = createCachedSelector(
  (state: RootState) => ({
    obj: getPayeeTransactionsById(state),
    deep: false,
  }),
  (state: RootState) => ({
    obj: accountsSelectors.getAccountsById(state),
    deep: false,
  }),
  (state: RootState, props) => ({
    obj: props && props.accountIds && props.accountIds.size > 0 ? props.accountIds.sort() : undefined,
    deep: true,
  }),
  (
    { obj: payeeTransactionsById },
    { obj: accountsById },
    { obj: accountIds },
  ) => {
    log.log('Selector - Getting Payees for ', accountIds);

    let payeeList = ImmutableMap();

    let accounts = accountIds;
    if (!accountIds) {
      accounts = accountsById.map((x) => x.id);
    }
    accounts.forEach((accountId) => {
      const pList = payeeTransactionsById.get(accountId);
      if (pList) {
        payeeList = payeeList.mergeWith((payee1: Payee, payee2: Payee) =>
          DateTime.fromISO(payee2.txn?.postedOn) >= DateTime.fromISO(payee1.txn?.postedOn) ? payee2 : payee1, pList);
      }
    });

    // Now we trim the payee list down if it is too large based on dates
    payeeList = payeeList.sort((payee1: Payee, payee2: Payee) =>
      DateTime.fromISO(payee2.txn?.postedOn).toSeconds() - DateTime.fromISO(payee1.txn?.postedOn).toSeconds());

    // Now we sort it by payee name so the list is alphabetized
    payeeList = payeeList.take(150).sort((payee1: Payee, payee2: Payee) => {
      if (payee1.name > payee2.name) {
        return 1;
      }
      if (payee1.name < payee2.name) {
        return -1;
      }
      return 0;
    });

    return payeeList;
  },
)({
  keySelector: (_state, props) => `${JSON.stringify(props && props.accountIds && props.accountIds.size > 0 ? props.accountIds.sort() : undefined)}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
  selectorCreator: selectorsHelpers.createFlexEqualSelector,
});

export const getPayeesAsList = createSelector(
  (state, props) => getPayeesForAccounts(state, props),
  (state) => getMemorizedRulesById(state),
  (state) => configFeatureFlagsSelectors.getFeatureFlagForUserOrDataset(
    state,
    ClientConfigFlags.memorizedPayeeAutoPopulate,
  ),
  (state) => isApplyMemorizedRulesEnabled(state),
  (state) => authSelectors.getIsWinDataset(state),
  (payeesForAccounts, memorizedPayees, memorizedPayeeAutoPopulate, applyMemorizedRules, isQWinDataset) => {
    const txnPayeeList = payeesForAccounts?.toList() ?? List();

    // add memorized payee to payee list & remove redendant payee from txns list
    const addMemorizedPayees = () => {
      const memorizedPayeeList = memorizedPayees.valueSeq().toArray().map((memPayee) => {
        const { coa, split } = memPayee;

        const newSplitItems = split?.items.map((item) => ({ ...item, id: null }));
        const txn = {
          coa,
          tags: memPayee.tags,
          amount: memPayee.amount,
          memo: memPayee.memo,
          ...(newSplitItems ? { split: transactionsTransformers.mkSplit({ items: newSplitItems }) } : {}),
        };
        const pRule = {
          id: memPayee.id,
          name: memPayee.payee,
          amountString: memPayee.amount,
          catString: transactionsUtils.isSplitTxn(txn) ? getSplitCategoryNames(txn) : getFieldString('category', txn, undefined),
          showSplitIcon: newSplitItems?.length > 1,
          isAmountBasedRule: Boolean(memPayee.amountCondition),
          isMemorizedPayee: true,
          markAsCleared: memPayee.markAsCleared,
          txn,
        };
        return Record(pRule)(pRule);
      });
      
      if (isQWinDataset) {
        return memorizedPayeeList;
      }
      
      const memorizedNamesSet = new Set(memorizedPayeeList.map((payee) => payee.name));
      const filteredTxnPayees = txnPayeeList
        .filter((payee) => !memorizedNamesSet.has(payee.name))
        .toArray();
      return [...memorizedPayeeList, ...filteredTxnPayees];
    };

    // Enable memorized payee auto-populate for QWIN only if quick fill enabled
    return memorizedPayeeAutoPopulate && ((isQWinDataset && applyMemorizedRules) || !isQWinDataset) ? addMemorizedPayees() : txnPayeeList;
  },
);
